import React from 'react';
import { Clock, Mail } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useBoundStore } from '../../store';
import { useI18n } from '../../i18n';

const formatTokens = (n: number): string => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}k`;
  return String(n);
};

const TrialBanner: React.FC = () => {
  const user = useBoundStore((state) => state.user);
  const { t } = useI18n();
  const trial = user?.trial;

  if (!trial) return null;

  const overLimit = trial.isOverTokenLimit;
  const expired = trial.isExpired;
  const daysLeft = trial.daysRemaining;

  // Pick tone: red when over limit / expired, amber when last 2 days, green otherwise
  const tone =
    overLimit || expired
      ? 'red'
      : daysLeft <= 2
      ? 'amber'
      : 'green';

  const palette = {
    red: { border: 'border-red-500', bg: 'bg-red-50', icon: 'text-red-600', text: 'text-red-800', meter: 'bg-red-500' },
    amber: { border: 'border-yellow-500', bg: 'bg-yellow-50', icon: 'text-yellow-700', text: 'text-yellow-800', meter: 'bg-yellow-500' },
    green: { border: 'border-emerald-500', bg: 'bg-emerald-50', icon: 'text-emerald-700', text: 'text-emerald-900', meter: 'bg-emerald-600' },
  }[tone];

  const headline = overLimit
    ? t.access.trial.tokenLimitReached
    : expired
    ? t.access.trial.ended
    : daysLeft === 0
    ? t.access.trial.endsToday
    : t.access.trial.daysLeft(daysLeft);

  return (
    <Alert className={`mb-4 ${palette.border} ${palette.bg}`}>
      <Clock className={`h-4 w-4 ${palette.icon}`} />
      <AlertDescription>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex-1">
            <p className={`font-semibold ${palette.text}`}>{headline}</p>
            {trial.tokenLimit != null && (
              <div className="mt-2 max-w-md">
                <div className={`flex items-center justify-between text-xs ${palette.text}`}>
                  <span>
                    {t.access.trial.tokensUsed(formatTokens(trial.tokensConsumed), formatTokens(trial.tokenLimit))}
                  </span>
                  <span>{trial.percentUsed}%</span>
                </div>
                <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-white/60">
                  <div
                    className={`h-full ${palette.meter}`}
                    style={{ width: `${Math.min(100, trial.percentUsed)}%` }}
                  />
                </div>
              </div>
            )}
          </div>
          {(overLimit || expired || daysLeft <= 2) && (
            <Button
              variant="outline"
              size="sm"
              className={`${palette.border} ${palette.text} hover:bg-white`}
              asChild
            >
              <a href="mailto:hello@queri.law?subject=Upgrade%20from%20Queri%20trial">
                <Mail className="mr-2 h-4 w-4" />
                {t.access.trial.upgrade}
              </a>
            </Button>
          )}
        </div>
      </AlertDescription>
    </Alert>
  );
};

export default TrialBanner;
